import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from "@auth0/auth0-react";
import { Grid, Box } from "@mui/material";

import sitesAPI from 'services/api/sites';
import groupAPI from 'services/api/groups';
import devicesAPI from 'services/api/bks';

import { loadingScreen } from "utils/wrapper";
import { setSiteFeatures } from "utils/map";
import { useSoftUIController } from "hooks/context";

import SoftBox from 'components/SoftComponents/SoftBox';
import SoftTypography from 'components/SoftComponents/SoftTypography';
import PolygonCreator from './components/polygonCreator';
import GroupSidebar from './components/groupSidebar';
import { setDeviceFeatures } from 'utils/map';
import { processDeviceData } from 'utils/device';

function NewGroupView({ user, localMap, isMapLoaded, setAddNewGroup, editGroupId, setEditGroupId, setSplitMap }) {
    const [controller] = useSoftUIController();
    const { selectedOrg, userPermissions } = controller;
    const navigate = useNavigate();
    const auth0 = useAuth0();

    sitesAPI.loadToken(auth0);
    groupAPI.loadToken(auth0);
    devicesAPI.loadToken(auth0);

    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    const sites = useQuery({ queryKey: ['getAllSites', selectedOrg], queryFn: async () => await sitesAPI.getAllSites() });
    const groups = useQuery({ queryKey: ['getAllGroups', selectedOrg], queryFn: async () => await groupAPI.getAllGroups() });
    const devices = useQuery({ queryKey: ['getAllDevices', selectedOrg], queryFn: async () => await devicesAPI.getAllDevices() });

    const editGroup = useMemo(() => {
        if (!editGroupId || !groups.data) return null;
        return groups.data.find(g => g.group_id === editGroupId || g._id === editGroupId);
    }, [editGroupId, groups.data]);

    const [appMode, setAppMode] = useState('edit');
    const [groupType, setGroupType] = useState('campaigns');
    const [selectedColor, setSelectedColor] = useState(null);

    const [selectedSiteIds, setSelectedSiteIds] = useState([]);
    const [generatedSiteIds, setGeneratedSiteIds] = useState([]);
    const [finalPolygonData, setFinalPolygonData] = useState(null);
    const [generateTrigger, setGenerateTrigger] = useState(0);

    const [selectedDeviceUUIDs, setSelectedDeviceUUIDs] = useState([]);
    const [generatedDeviceUUIDs, setGeneratedDeviceUUIDs] = useState([]);

    const hasInitialized = useRef(false);
    const prevGroupType = useRef('campaigns');

    useEffect(() => {
        if (!hasInitialized.current && sites.data && devices.data) {
            if (editGroup) {
                const initialType = editGroup.type || 'campaigns';
                prevGroupType.current = initialType;
                setGroupType(initialType);

                if (initialType === 'monitoring') {
                    const devIds = editGroup.devices ? editGroup.devices.map(d => d.uuid || d) : [];
                    setSelectedDeviceUUIDs(devIds);
                    setGeneratedDeviceUUIDs(devIds);
                } else {
                    const siteIds = editGroup.sites ? editGroup.sites.map(s => s.site_id || s) : [];
                    setSelectedSiteIds(siteIds);
                    setGeneratedSiteIds(siteIds);
                }

                if (editGroup.geojson && editGroup.geojson.length > 0) {
                    setFinalPolygonData(editGroup.geojson[0]);
                }

                setSelectedColor(editGroup.color || editGroup.properties?.feature_colour || '');
            }
            hasInitialized.current = true;
        }
    }, [editGroup, sites.data, devices.data]);

    useEffect(() => {
        if (hasInitialized.current && prevGroupType.current !== groupType) {
            setSelectedSiteIds([]);
            setGeneratedSiteIds([]);
            setSelectedDeviceUUIDs([]);
            setGeneratedDeviceUUIDs([]);
            setFinalPolygonData(null);
            setAppMode('edit');
            setGenerateTrigger(0);
            prevGroupType.current = groupType;
        }
    }, [groupType]);


    useEffect(() => {
        if (setSplitMap) setSplitMap(groupType === 'monitoring');
        return () => { if (setSplitMap) setSplitMap(false); };
    }, [groupType, setSplitMap]);

    const masterSites = useMemo(() => {
        if (!sites.data) return [];
        return sites.data.map(s => setSiteFeatures(s, showAdvancedDeviceLevel)).sort((a, b) => a.properties.zIndex - b.properties.zIndex);
    }, [sites.data, showAdvancedDeviceLevel]);

    const styledSites = useMemo(() => masterSites.map(ss => ({
        ...ss,
        properties: {
            ...ss.properties,
            feature_colour: selectedSiteIds.includes(ss.properties.site_id) ? '#344767' : '#8392ab'
        }
    })), [masterSites, selectedSiteIds]);

    const masterDevices = useMemo(() => {
        if (!devices.data) return [];
        return devices.data.map(d => setDeviceFeatures(processDeviceData(devices.dataUpdatedAt, d, false), showAdvancedDeviceLevel));
    }, [devices.data, showAdvancedDeviceLevel]);

    const styledDevices = useMemo(() => masterDevices.map(dd => ({
        ...dd,
        properties: {
            ...dd.properties,
            feature_colour: selectedDeviceUUIDs.includes(dd.properties.uuid) ? '#344767' : '#8392ab'
        }
    })), [masterDevices, selectedDeviceUUIDs]);

    const isModified = useMemo(() => {
        const currentSelected = groupType === 'monitoring' ? selectedDeviceUUIDs : selectedSiteIds;
        const currentGenerated = groupType === 'monitoring' ? generatedDeviceUUIDs : generatedSiteIds;

        if (currentSelected.length === 0) return false;
        if (currentSelected.length !== currentGenerated.length) return true;

        const sortedSelected = [...currentSelected].sort();
        const sortedGenerated = [...currentGenerated].sort();
        return sortedSelected.some((id, index) => id !== sortedGenerated[index]);
    }, [groupType, selectedSiteIds, generatedSiteIds, selectedDeviceUUIDs, generatedDeviceUUIDs]);

    const handlePolygonEnclosure = useCallback((enclosedIds) => {
        if (appMode !== 'review') setSelectedSiteIds(enclosedIds);
    }, [appMode]);

    const handleDeviceEnclosure = useCallback((enclosedIds) => {
        if (appMode !== 'review') setSelectedDeviceUUIDs(enclosedIds);
    }, [appMode]);

    const handleRemoveSite = useCallback((site_id) => {
        setSelectedSiteIds((prev) => prev.filter(id => id !== site_id));
    }, []);

    const handleSiteToggle = useCallback((site_id) => {
        if (appMode === 'review') return;
        setSelectedSiteIds((prev) => prev.includes(site_id) ? prev.filter(id => id !== site_id) : [...prev, site_id]);
    }, [appMode]);

    const handleDeviceToggle = useCallback((uuid) => {
        if (appMode === 'review') return;
        setSelectedDeviceUUIDs((prev) => prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]);
    }, [appMode]);

    const selectedDevicesList = useMemo(() => {
        if (!masterDevices) return [];
        return masterDevices.map(d => d.properties).filter(d => selectedDeviceUUIDs.includes(d.uuid));
    }, [masterDevices, selectedDeviceUUIDs]);

    const handleGenerateFinalPolygon = useCallback(() => {
        if (groupType === 'monitoring') {
            if (selectedDeviceUUIDs.length === 0) return alert("Select at least one device first.");
            setGeneratedDeviceUUIDs([...selectedDeviceUUIDs]);
        } else {
            if (selectedSiteIds.length === 0) return alert("Select at least one site first.");
            setGeneratedSiteIds([...selectedSiteIds]);
        }

        setGenerateTrigger(prev => prev + 1);
        setAppMode('review');
    }, [groupType, selectedDeviceUUIDs, selectedSiteIds]);

    const handlePolygonGenerated = useCallback((geoJson) => {
        setFinalPolygonData(geoJson);
    }, []);

    const handleEditMode = useCallback(() => {
        setAppMode('edit');
    }, []);

    const handleCancel = useCallback(() => {
        setSelectedSiteIds([]); setGeneratedSiteIds([]);
        setSelectedDeviceUUIDs([]); setGeneratedDeviceUUIDs([]);
        setFinalPolygonData(null); setAddNewGroup(false);
        if (setEditGroupId) setEditGroupId(null);
    }, [navigate, setAddNewGroup, setEditGroupId]);

    if (sites.isLoading || groups.isLoading || devices.isLoading) {
        return (
            <Box sx={{ gridColumn: 1, gridRow: '1 / span 2', height: '100%', overflow: 'hidden', display: 'flex' }}>
                <SoftBox sx={{
                    width: { xxs: '100%', sm: '350px', md: '400px', lg: '450px', xl: '500px', xxl: '25vw' },
                    maxWidth: { xxl: '600px' },
                    flexShrink: 0,
                    height: '100%', backgroundColor: '#f8f9fa',
                    borderRight: { md: '1px solid #ddd' }, borderBottom: { xxs: '1px solid #ddd', md: 'none' }
                }}>
                    {loadingScreen(controller, true)}
                </SoftBox>
            </Box>
        );
    }

    if (sites.error || groups.error || devices.error) return "An error has occurred";

    return (
        <>
            <Box sx={{ gridColumn: 1, gridRow: '1 / span 2', height: '100%', overflow: 'hidden', display: 'flex' }}>
                <GroupSidebar
                    appMode={appMode}
                    allSites={styledSites}
                    selectedSiteIds={selectedSiteIds}
                    onRemoveSite={handleRemoveSite}
                    onGeneratePolygon={handleGenerateFinalPolygon}
                    onEditMode={handleEditMode}
                    onCancel={handleCancel}
                    onSuccess={handleCancel}
                    selectedColor={selectedColor}
                    setSelectedColor={setSelectedColor}
                    showGenerateButton={isModified}
                    finalPolygonData={finalPolygonData}
                    editGroup={editGroup}
                    setEditGroupId={setEditGroupId}

                    groupType={groupType}
                    setGroupType={setGroupType}
                    allDevices={styledDevices}
                    selectedDeviceUUIDs={selectedDeviceUUIDs}
                    onRemoveDevice={handleDeviceToggle}
                />
            </Box>

            {groupType === 'monitoring' && (
                <SoftBox sx={{ gridColumn: { md: 2 }, gridRow: { xxs: 3, md: 2 }, backgroundColor: '#f8f9fa', overflowY: 'auto', p: 2, borderTop: '2px solid #ddd' }}>
                    <SoftTypography variant="h5" mb={1.5}>Select Devices</SoftTypography>
                    <Grid container spacing={2}>
                        {masterDevices.map(feature => {
                            const device = feature.properties;
                            const isSelected = selectedDeviceUUIDs.includes(device.uuid);
                            return (
                                <Grid item xxs={12} sm={6} md={4} xl={3} key={device.uuid}>
                                    <SoftBox
                                        onClick={() => handleDeviceToggle(device.uuid)}
                                        p={1.5}
                                        sx={{
                                            backgroundColor: isSelected ? (selectedColor || '#17c1e8') : '#fff',
                                            borderRadius: '8px', cursor: 'pointer',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                            border: isSelected ? `2px solid ${selectedColor || '#17c1e8'}` : '2px solid transparent',
                                            transition: 'all 0.2s ease',
                                            height: '100%', display: 'flex', flexDirection: 'column'
                                        }}
                                    >
                                        <SoftTypography fontWeight="bold" fontSize="14px" color={isSelected ? "white" : "dark"}>{device.name}</SoftTypography>
                                        <SoftTypography fontSize="12px" color={isSelected ? "white" : "text"} sx={{ mt: 0.5 }}>UUID: {device.uuid}</SoftTypography>
                                        <SoftTypography fontSize="12px" color={isSelected ? "white" : "text"}>Serial No: {device.serial_number || device.serial || 'N/A'}</SoftTypography>
                                    </SoftBox>
                                </Grid>
                            )
                        })}
                    </Grid>
                </SoftBox>
            )}

            <PolygonCreator
                localMap={localMap}
                isMapLoaded={isMapLoaded}
                user={user}
                appMode={appMode}
                generateTrigger={generateTrigger}
                selectedColor={selectedColor}
                onPolygonGenerated={handlePolygonGenerated}
                editGroup={editGroup}
                groupType={groupType}

                points={groupType === 'monitoring' ? styledDevices : styledSites}
                selectedPointIds={groupType === 'monitoring' ? selectedDeviceUUIDs : selectedSiteIds}
                idField={groupType === 'monitoring' ? 'uuid' : 'site_id'}
                nameField={groupType === 'monitoring' ? 'name' : 'site_name'}
                onPointsEnclosed={groupType === 'monitoring' ? handleDeviceEnclosure : handlePolygonEnclosure}
                onPointToggle={groupType === 'monitoring' ? handleDeviceToggle : handleSiteToggle}
            />
        </>
    );
}

NewGroupView.propTypes = {
    user: PropTypes.object, localMap: PropTypes.object, isMapLoaded: PropTypes.bool,
    setAddNewGroup: PropTypes.func, editGroupId: PropTypes.string, setEditGroupId: PropTypes.func, setSplitMap: PropTypes.func
};

export default NewGroupView;